import { fg } from '@atlaskit/platform-feature-flags';

/**
 * Tracks which uncategorized storage-item keys have already emitted an
 * `uncategorizedWebStorageItemSet` track event this session, so repeated writes
 * of the same unknown key don't emit duplicate events.
 *
 * State is module-level (per page load), which gives us the "per session"
 * semantic for free — it resets naturally on reload. Same pattern as
 * `ItemKeyCache` (item-key-cache-service) and the logger error-event throttle.
 *
 * Keys are scoped by storage type so the same key name in different storage
 * types (e.g. a cookie and a localStorage item both named `foo`) remain
 * distinct reportable signals.
 */
class UncategorizedItemReportCacheService {
	private reportedKeys: Set<string> = new Set<string>();

	private compositeKey(storageType: string, itemKey: string): string {
		return `${storageType}::${itemKey}`;
	}

	markReported(storageType: string, itemKey: string): void {
		this.reportedKeys.add(this.compositeKey(storageType, itemKey));
	}

	wasReported(storageType: string, itemKey: string): boolean {
		return this.reportedKeys.has(this.compositeKey(storageType, itemKey));
	}

	// Test-only helper to reset the per-process cache between tests.
	__reset(): void {
		this.reportedKeys.clear();
	}
}

let cacheInstance: UncategorizedItemReportCacheService | undefined;

/**
 * Lazily creates the cache on first use. The dedup helpers only call this after confirming the
 * dedup gate is on, so when the gate is off the cache is never instantiated.
 */
function getCache(): UncategorizedItemReportCacheService {
	if (!cacheInstance) {
		cacheInstance = new UncategorizedItemReportCacheService();
	}
	return cacheInstance;
}

// Test-only helper to reset the per-process cache between tests.
export function __resetUncategorizedItemReportCache(): void {
	cacheInstance = undefined;
}

/**
 * Whether an `uncategorizedWebStorageItemSet` event should be emitted for this key. Called at the
 * event fire sites before `sendTrackEvent`. When the dedup gate is on, returns false once the key
 * has already been reported this session (suppressing duplicates); when the gate is off it always
 * returns true (unchanged behaviour) without touching the cache.
 */
export function shouldReportUncategorizedItemSet(storageType: string, itemKey: string): boolean {
	if (!fg('platform_bsc_dedup_uncategorized_items')) {
		return true;
	}
	return !getCache().wasReported(storageType, itemKey);
}

/**
 * Returns the `onSent` callback for `sendTrackEvent`, which marks the key as reported only when the
 * event actually sends (passes sampling). This "mark on send" means a key whose event was dropped
 * by sampling can still emit on a later write, so frequently-set keys are reliably reported once
 * rather than lost to sampling. Returns undefined when the dedup gate is off (no marking).
 */
export function uncategorizedItemSetOnSent(
	storageType: string,
	itemKey: string,
): (() => void) | undefined {
	if (!fg('platform_bsc_dedup_uncategorized_items')) {
		return undefined;
	}
	return () => getCache().markReported(storageType, itemKey);
}
